import { useCallback, useMemo } from 'react';
import { usePermissions } from 'hooks/permissions/usePermissions';
import { Permission } from 'shared/domain/permissions/types';
import { PermissionTranslationKeys } from 'shared/domain/permissions/translationKeys';

import { useInspectionStore } from '../store';
import { useInspection } from './useInspection';

export function useInspectionEditPermission(): Permission {
  const { canEditInspection } = usePermissions();
  const { inspection } = useInspection();

  return useMemo(
    () =>
      canEditInspection(
        Boolean(inspection?.isCompleted),
        Boolean(inspection?.deleted)
      ),
    [canEditInspection, inspection?.isCompleted, inspection?.deleted]
  );
}

/**
 * Disables fields when edit is forbidden — but only in contexts that enforce
 * read-only permissions (atomic edit view, or an already-archived inspection).
 * Create wizard (atomicSaveEnabled=false, no deleted inspection) stays editable
 * even if the role would fail canEditInspection, matching the former
 * `atomicMode && !editPermission.permission` guard in Form.
 */
export function useInspectionFieldDisabled(): (
  fieldDisabled?: boolean | { reason: string } | false
) => boolean | { reason: string } {
  const editPermission = useInspectionEditPermission();
  const atomicSaveEnabled = useInspectionStore.use.atomicSaveEnabled();
  const { inspection } = useInspection();

  return useCallback(
    (fieldDisabled: boolean | { reason: string } | false = false) => {
      const enforceEditPermission =
        atomicSaveEnabled || Boolean(inspection?.deleted);

      if (enforceEditPermission && !editPermission.permission) {
        return {
          reason:
            editPermission.reason ??
            PermissionTranslationKeys.ACTION_FORBIDDEN,
        };
      }

      return fieldDisabled;
    },
    [atomicSaveEnabled, editPermission, inspection?.deleted]
  );
}

/** True when a field disabled value means the control should not accept input. */
export function isInspectionFieldDisabled(
  disabled: boolean | { reason: string } | false | undefined
): boolean {
  return Boolean(disabled);
}
